import { request } from "@/api/client";
import type {
  PressAnalytics,
  PressContact,
  PressPublication,
  PressRelease,
  PressArticle,
  SentimentTrendPayload,
  TopOutlet,
  PressFeed,
  DraftAssistantPayload,
  DraftAssistantResponse,
} from "@/types/publicist";

export function createPressDraft(payload: {
  title: string;
  artist: string;
  content: string;
  created_by: string;
  release_date?: string | null;
  send_by?: string | null;
}) {
  return request(`/publicist/draft`, {
    method: "POST",
    body: payload,
  });
}

export function fetchPressReleases() {
  return request<PressRelease[]>("/publicist/releases");
}

export function fetchPressPublications(releaseId: number) {
  return request<PressPublication[]>(`/publicist/releases/${releaseId}/publications`);
}

export function fetchPressVersions(releaseId: number) {
  return request(`/publicist/releases/${releaseId}/versions`);
}

export function updatePressPublicationStatus(payload: {
  publication_id: number;
  new_status: string;
  changed_by: string;
  reply_snippet?: string | null;
  coverage_url?: string | null;
}) {
  return request(`/publicist/publications/update_status`, {
    method: "POST",
    body: payload,
  });
}

export function fetchPressAnalytics(releaseId: number) {
  return request<PressAnalytics>(`/publicist/analytics/${releaseId}`);
}

export function fetchSentimentTrends(days = 30) {
  return request<SentimentTrendPayload>(`/publicist/analytics/trends?days=${days}`);
}

export function fetchTopOutlets(limit = 10) {
  return request<{ outlets: TopOutlet[] }>(`/publicist/analytics/top_outlets?limit=${limit}`);
}

export function sendPressCampaign(payload: { release_id: number; publication_ids: number[]; template_id?: string | null }) {
  return request(`/publicist/send_campaign`, {
    method: "POST",
    body: payload,
  });
}

export function fetchPressContacts(limit = 100) {
  return request<PressContact[]>(`/publicist/contacts?limit=${limit}`);
}

export function ingestPressContacts(leads: Array<Record<string, unknown>>) {
  return request(`/publicist/contacts/ingest`, {
    method: "POST",
    body: { leads },
  });
}

export function fetchPressroom() {
  return request<Array<{ title: string; artist: string; release_date?: string | null; summary: string }>>("/publicist/pressroom");
}

export function requestPressReview(releaseId: number, payload: { requested_by: string; notes?: string | null }) {
  return request(`/publicist/releases/${releaseId}/request_review`, {
    method: "POST",
    body: payload,
  });
}

export function approvePressRelease(releaseId: number, payload: { editor: string; content: string }) {
  return request(`/publicist/approve`, {
    method: "POST",
    body: { release_id: releaseId, ...payload },
  });
}

export function fetchPressArticles(params: {
  artist?: string;
  sentiment?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params.artist) query.set("artist", params.artist);
  if (params.sentiment) query.set("sentiment", params.sentiment);
  if (params.start_date) query.set("start_date", params.start_date);
  if (params.end_date) query.set("end_date", params.end_date);
  query.set("limit", String(params.limit ?? 50));
  query.set("offset", String(params.offset ?? 0));
  return request<{ total: number; limit: number; offset: number; results: PressArticle[] } | PressArticle[]>(`/publicist/press/articles?${query.toString()}`);
}

export function ingestPressSource(payload: { url: string; artist?: string; genre?: string; tags?: string[] }) {
  return request(`/publicist/press/ingest`, {
    method: "POST",
    body: payload,
  });
}

export function runPressDigestor(leads?: Array<Record<string, unknown>>) {
  return request(`/publicist/press/digestor/run`, {
    method: "POST",
    body: { leads: leads ?? [] },
  });
}

export function fetchPressFeeds() {
  return request<PressFeed[]>(`/publicist/press/feeds`);
}

export function addPressFeed(payload: { url: string; label?: string | null }) {
  return request(`/publicist/press/feeds`, {
    method: "POST",
    body: payload,
  });
}

export function deletePressFeed(feedId: number) {
  return request(`/publicist/press/feeds/${feedId}`, {
    method: "DELETE",
  });
}

export function generateAIDraft(releaseId: number, payload: DraftAssistantPayload) {
  return request<DraftAssistantResponse>(`/publicist/releases/${releaseId}/draft`, {
    method: "POST",
    body: payload,
  });
}

export function reviseAIDraft(releaseId: number, payload: DraftAssistantPayload) {
  return request<DraftAssistantResponse>(`/publicist/releases/${releaseId}/revise`, {
    method: "POST",
    body: payload,
  });
}

export function generateOutletVariants(releaseId: number, payload: DraftAssistantPayload) {
  return request<DraftAssistantResponse>(`/publicist/releases/${releaseId}/variants`, {
    method: "POST",
    body: payload,
  });
}

export function autoFillTemplateVariables(releaseId: number, payload: DraftAssistantPayload) {
  return request<DraftAssistantResponse>(`/publicist/releases/${releaseId}/auto-fill`, {
    method: "POST",
    body: payload,
  });
}
